import { hasPendingResubmit } from '@l2u/shared/resubmitTurn';
import type { Company } from '@l2u/web-shared';

type InfoTurnFields = Pick<
  Company,
  'info_review_status' | 'info_reviewer_comment' | 'info_reviewed_at' | 'info_resubmitted_at'
>;

/** What the company owes on its company information, if anything. */
export type CompanyInfoAction = 'rejected' | 'comment';

/**
 * The admin's PUBLISHED hand-back on the company-information block, ignoring
 * whether the company has edited since:
 *  * 'rejected' — a Not Compliant.
 *  * 'comment'  — a published Comment decision. 'comment' carries the status
 *    forward, so on a block still under review it reads `pending` + a comment
 *    + a reviewed_at stamp. A comment on an approved / N/A block is a note on
 *    finished work, never an action — the question rule (isAwaitingVendor).
 *    reviewed_at is required: a pending block with no stamp was never reviewed.
 */
function companyInfoHandBack(company: InfoTurnFields | null | undefined): CompanyInfoAction | null {
  if (!company) return null;
  if (company.info_review_status === 'rejected') return 'rejected';
  if (
    company.info_review_status === 'pending' &&
    !!company.info_reviewer_comment?.trim() &&
    company.info_reviewed_at != null
  ) {
    return 'comment';
  }
  return null;
}

/**
 * THE vendor predicate: the company owes a response on its company information
 * — a published Not Compliant, or a published admin comment, the company has
 * NOT edited since. Once it has (info_resubmitted_at after the verdict, the
 * shared resubmit rule) the turn is the admin's, exactly as the status rollup
 * and the admin page read it. The company has no reply thread on this block,
 * so editing the details IS the reply. Every vendor signal (nav badge,
 * questionnaire banner line, Needs-action empty state, the verdict notice)
 * goes through this one function.
 */
export function companyInfoVendorAction(
  company: InfoTurnFields | null | undefined,
): CompanyInfoAction | null {
  const handBack = companyInfoHandBack(company);
  if (!handBack || !company) return null;
  const resubmitted = hasPendingResubmit({
    resubmitted_at: company.info_resubmitted_at ?? null,
    reviewed_at: company.info_reviewed_at ?? null,
  });
  return resubmitted ? null : handBack;
}

export function companyInfoNeedsVendorAction(company: InfoTurnFields | null | undefined): boolean {
  return companyInfoVendorAction(company) !== null;
}

/** Sent back or commented on, and the company has since edited: waiting on the admin. */
export function companyInfoAwaitingAdmin(company: InfoTurnFields | null | undefined): boolean {
  return companyInfoHandBack(company) !== null && !companyInfoNeedsVendorAction(company);
}

/**
 * The company-information fields lock once the company is under review or
 * compliant — EXCEPT when the reviewer has handed the block back (a published
 * Not Compliant or comment on the company-information verdict, 0060): the
 * company must be able to fix what was asked, including an ABN the registry
 * reports inactive. Like a Not Compliant, a comment keeps the block open after
 * the first edit, until the admin's next verdict — the company may need more
 * than one save to get it right. (UI only: the vendor PATCH enforces no lock.)
 */
export function companyInfoFieldsLocked(
  company: Pick<Company, 'status' | 'info_review_status'> & Partial<InfoTurnFields>,
): boolean {
  if (companyInfoHandBack(company) !== null) return false;
  return company.status === 'review' || company.status === 'compliant';
}

/**
 * The company-information share of the vendor's "needs your action" signals:
 * 1 while the company owes a response (companyInfoNeedsVendorAction), else 0.
 * Deliberately NOT part of vendorActionBadgeCount — that count is
 * question-scoped and shared with the driver portal — so the company shell
 * badges its own Company info nav item with this instead.
 */
export function companyInfoActionCount(company: InfoTurnFields | null | undefined): number {
  return companyInfoNeedsVendorAction(company) ? 1 : 0;
}

/** The screen-reader text for that badge. */
export const COMPANY_INFO_ACTION_SR = 'company information needs your action';

/**
 * The company sidebar's badge for one nav item: the question-scoped action
 * count on the Questionnaire item, the company-information action on the
 * Company info item (with its own screen-reader sentence), the server's nav
 * counts on the items `countBadges` covers (Training, Drivers, Assets,
 * Corrective actions, File notes, Library — lib/companyNavCounts.ts), nothing
 * elsewhere.
 */
export function companyNavBadge(
  to: string,
  ctx: {
    questionnaireTo: string;
    infoTo: string;
    actionCount: number;
    company: InfoTurnFields | null | undefined;
    countBadges?: ReadonlyMap<string, { count: number; srText: string }>;
  },
): { count: number; srText?: string } {
  if (to === ctx.questionnaireTo) return { count: ctx.actionCount };
  if (to === ctx.infoTo) {
    return { count: companyInfoActionCount(ctx.company), srText: COMPANY_INFO_ACTION_SR };
  }
  return ctx.countBadges?.get(to) ?? { count: 0 };
}
